import { Skeleton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import {
  getListJobsQueryKey,
  useGetCatalog,
} from "../../../api/generated/endpoints/generation/generation";
import type { GenerationType } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import {
  SoundsComposer,
  soundsOffered,
} from "../../../platform/sounds/composer/SoundsComposer";
import { CreateFrame } from "./CreateFrame";
import home from "../pro-home.module.css";
import classes from "./create.module.css";
import { RecentResults } from "./RecentResults";

/** The kinds the Sounds page's My Generations reads, in its order, so the two share the read. */
const SOUND_TYPES: readonly GenerationType[] = ["audio.sfx", "audio.tts", "audio.music"];

/**
 * The prototype's `#create-sound`: the Sounds page's composer in the card (its Voiceover, Sound
 * effect and Music the composer's three modes), then the recent sounds.
 */
export function CreateSoundScreen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/studio/pro/create-sound",
  });
  const queryClient = useQueryClient();
  const catalog = useGetCatalog();
  // The composer's own rule, so the card and the composer never disagree.
  const offered = catalog.data === undefined ? undefined : soundsOffered(catalog.data);
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={classes.composerSkeleton} />
  ) : offered === true ? (
    // A brand's own draft, as the Studio's composer is keyed (decision 20).
    <SoundsComposer
      key={brandId}
      catalog={catalog.data}
      // The Sounds page's own: its lists are read again once a sound starts.
      onStarted={() =>
        void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) })
      }
      inline
      styles
    />
  ) : (
    <p className={classes.dim}>No model makes sounds yet.</p>
  );
  return (
    <CreateFrame
      composer={composer}
      results={
        <RecentResults
          teamId={teamId}
          brandId={brandId}
          types={SOUND_TYPES}
          noun="voice and sound"
          // The Sounds page's Library tab, the brand's sounds, in Pro (spec ui-switch, e5b).
          more={
            <Link
              to="/teams/$teamId/brands/$brandId/pro/sounds"
              params={{ teamId, brandId }}
              className={home.subtle}
            >
              Sounds library <ArrowRight size={14} aria-hidden />
            </Link>
          }
        />
      }
    />
  );
}
